import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, PlusIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { getAllDomainConfigs, ApiDomainConfig } from '../../services/domainConfigService';

const DomainConfigs: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<ApiDomainConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getAllDomainConfigs()
      .then((data) => setConfigs(data))
      .catch(() => setError(t('domainConfig.errorLoading')))
      .finally(() => setLoading(false));
  }, [t]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('domainConfig.title')}</h1>
          <p className="text-slate-600 mt-1">{t('domainConfig.subtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/add-domain-config')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all"
        >
          <PlusIcon className="size-5" />
          <span>{t('domainConfig.addDomain')}</span>
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
            <p className="mt-2 text-slate-600">{t('domainConfig.loadingDomains')}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && configs.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <GlobeAltIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-4 text-lg font-semibold text-slate-900">{t('domainConfig.noDomains')}</h3>
          <p className="mt-2 text-slate-600">{t('domainConfig.noDomainsDescription')}</p>
        </div>
      )}

      {!loading && !error && configs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {configs.map((cfg) => (
            <div
              key={cfg.id}
              className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Color bar */}
              <div
                className="h-2"
                style={{ backgroundColor: cfg.colors.primary }}
              />

              <div className="p-5 space-y-4">
                {/* Logo + Brand */}
                <div className="flex items-center gap-3">
                  <img
                    src={cfg.logos.dark}
                    alt={cfg.brandName}
                    className="h-10 w-auto max-w-[120px] object-contain"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-900 truncate">{cfg.brandName}</h3>
                    <p className="text-sm text-slate-500 truncate">{cfg.hostname}</p>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('domainConfig.pageTitle')}</span>
                    <span className="text-slate-700 truncate ml-2 max-w-[60%] text-right">{cfg.pageTitle}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('domainConfig.emailAccount')}</span>
                    <span className="text-slate-700 font-mono">{cfg.emailAccountSlug}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">{t('domainConfig.colors')}</span>
                    <div className="flex gap-1">
                      {[cfg.colors.primary, cfg.colors.secondary, cfg.colors.accent, cfg.colors.primaryHover].map((c, i) => (
                        <div
                          key={i}
                          className="w-5 h-5 rounded-full border border-slate-200"
                          style={{ backgroundColor: c }}
                          title={c}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Edit button */}
                <button
                  onClick={() => navigate(`/edit-domain-config/${cfg.id}`)}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors"
                >
                  <PencilIcon className="size-4" />
                  {t('common.edit')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DomainConfigs;
